import React, { useState } from "react"
import { Moment } from "moment"
import axios, { AxiosError } from "axios"
import { toast } from "react-toastify"
import {
     API_URL_ESPACE_AVENTURIER,
     CONST_DECLARATION_SUIVI_TYPE_INTERESSE_DEMANDE_REGUL,
     CONST_DECLARATION_SUIVI_TYPE_INTERESSE_RECTIFICATION_SANS_DEMANDE,
     CONST_DECLARATION_SUIVI_TYPE_INTERESSE_SIGNATURE,
     CONST_DECLARATION_SUIVI_TYPE_RESP_DEMANDE_RECTIFICATION,
     CONST_DECLARATION_SUIVI_TYPE_RESP_REJET_REGUL,
     CONST_DECLARATION_SUIVI_TYPE_RESP_VALIDATION,
     CONST_DECLARATION_SUIVI_TYPE_RESP_VALIDATION_REGUL,
     CONST_DECLARATION_SUIVI_TYPE_SIEGE_DEMANDE_RECTIFICATION,
     CONST_DECLARATION_SUIVI_TYPE_SIEGE_VALIDATION,
} from "../../../../constants"
import { IListingItemResponseModel } from "../core/models"
import { IConsultantDeclarationMensuelleFactureModel } from "@common-models/*"
import { ConsultantNdfDetailDocumentModel } from "../../../../models"
import { useConsultantDeclarationActions } from "../../../../app-consultant/modules/activites/components/declaration/core/_hooks"
import SaisieDeclarationConsultantST from "./Saisiedeclarationconsultantst"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"

interface Props {
     item: IListingItemResponseModel
     mois: Moment
     isExpanded: boolean
     onToggle: () => void
     onSuccess: () => void | Promise<void>
     onEdit: (defaultStep: string) => void
}

// ─── Status badge config ──────────────────────────────────────────────────────

const ETAT_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
     [CONST_DECLARATION_SUIVI_TYPE_INTERESSE_SIGNATURE]: { label: "Envoyé", color: "info", icon: "fa-check" },
     [CONST_DECLARATION_SUIVI_TYPE_RESP_VALIDATION]: { label: "Envoyé", color: "success", icon: "fa-paper-plane" },
     [CONST_DECLARATION_SUIVI_TYPE_RESP_VALIDATION_REGUL]: { label: "Régul. validée", color: "success", icon: "fa-check-double" },
     [CONST_DECLARATION_SUIVI_TYPE_RESP_DEMANDE_RECTIFICATION]: { label: "Rectification", color: "danger", icon: "fa-exclamation-circle" },
     [CONST_DECLARATION_SUIVI_TYPE_INTERESSE_RECTIFICATION_SANS_DEMANDE]: { label: "En rectification", color: "warning", icon: "fa-edit" },
     [CONST_DECLARATION_SUIVI_TYPE_INTERESSE_DEMANDE_REGUL]: { label: "Régul. demandée", color: "warning", icon: "fa-hand-paper" },
     [CONST_DECLARATION_SUIVI_TYPE_SIEGE_DEMANDE_RECTIFICATION]: { label: "Rectif. siège", color: "danger", icon: "fa-building" },
     [CONST_DECLARATION_SUIVI_TYPE_SIEGE_VALIDATION]: { label: "Validé siège", color: "success", icon: "fa-building" },
     [CONST_DECLARATION_SUIVI_TYPE_RESP_REJET_REGUL]: { label: "Régul. rejetée", color: "danger", icon: "fa-times-circle" },
}

const DeclarationsListNewItem = ({ item, mois, isExpanded, onToggle, onSuccess, onEdit }: Props) => {
     const { actions, mutations } = useConsultantDeclarationActions()
     const [isViewingDoc, setIsViewingDoc] = useState(false)
     const [isSending, setIsSending] = useState(false)

     const etatType = item.declaration?.etat?.type
     const etatConf = etatType ? ETAT_CONFIG[etatType] : null
     const hasFacture = item.facturesDePrestation.length > 0

     const canExpand =
          !item.declaration ||
          !etatType ||
          [CONST_DECLARATION_SUIVI_TYPE_INTERESSE_RECTIFICATION_SANS_DEMANDE, CONST_DECLARATION_SUIVI_TYPE_RESP_DEMANDE_RECTIFICATION, CONST_DECLARATION_SUIVI_TYPE_RESP_VALIDATION_REGUL].includes(
               etatType
          )

     // ── View document ──────────────────────────────────────────────────────────

     const handleViewDoc = (reference: string, mimeType: string) => {
          setIsViewingDoc(true)
          axios
               .post(`${API_URL_ESPACE_AVENTURIER}/download-document`, { reference }, { responseType: "arraybuffer" })
               .then(r => {
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: mimeType }))
                    window.open(url)
               })
               .catch((e: AxiosError) => {
                    toast.error((e.response?.data as any)?.detail || "Erreur lors du chargement du document.", { autoClose: false })
               })
               .finally(() => setIsViewingDoc(false))
     }

     // ── Actions ────────────────────────────────────────────────────────────────

     const handleEnvoiDeclaration = async () => {
          if (!item.declaration?.id) return
          if (item.facturesDePrestation.length === 0 && item.nbJoursTravailles <= 0) {
               toast.info("Merci de joindre une facture de prestation et de saisir le nombre de jours travaillés.")
               return
          }
          if (item.facturesDePrestation.length === 0) {
               toast.info("Merci de joindre une facture de prestation avant d'envoyer.")
               return
          }
          if (item.nbJoursTravailles <= 0) {
               toast.info("Merci de saisir le nombre de jours travaillés avant d'envoyer.")
               return
          }
          setIsSending(true)
          try {
               await actions.validationRespFournisseur(item.declaration.id, async () => {
                    toast.success("Déclaration envoyée")
                    await onSuccess()
               })
          } catch (e: any) {
               toast.error(e.response?.data?.detail, { autoClose: false })
               setIsSending(false)
          }
     }

     const handleRectifier = () => {
          if (!item.declaration?.id) return
          actions.entamerRectificationSansDemandeValidation(item.declaration.id, () => {
               onSuccess()
          })
     }

     const handleDemandeRegul = () => {
          if (!item.declaration?.id) return
          actions.demandeRegul(item.declaration.id, () => onSuccess())
     }

     // ── Action buttons by state ────────────────────────────────────────────────

     const renderActions = () => {
          if (!item.declaration) return null

          // No etat or rectification sans demande → Éditer + Envoyer
          if (!etatType || etatType === CONST_DECLARATION_SUIVI_TYPE_INTERESSE_RECTIFICATION_SANS_DEMANDE) {
               const canSend = item.facturesDePrestation.length > 0 && item.nbJoursTravailles > 0
               return (
                    <>
                         <button className="btn btn-sm btn-warning d-flex align-items-center gap-1" onClick={() => onEdit("recap")}>
                              <i className="fas fa-edit fs-7" /> Éditer
                         </button>
                         <button className={`btn btn-sm btn-${canSend ? "success" : "secondary"} d-flex align-items-center gap-1`} onClick={handleEnvoiDeclaration} disabled={isSending}>
                              {isSending ? <MySimpleSpinner size={"sm"} /> : <><i className="fas fa-paper-plane fs-7" /> Envoyer</>}
                         </button>
                    </>
               )
          }

          // Signed by consultant → Rectifier + Consulter
          if (etatType === CONST_DECLARATION_SUIVI_TYPE_INTERESSE_SIGNATURE) {
               return (
                    <>
                         <button className="btn btn-sm btn-warning d-flex align-items-center gap-1" onClick={handleRectifier}>
                              <i className="fas fa-edit fs-7" /> Rectifier
                         </button>
                         <button className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={() => onEdit("recap")}>
                              <i className="fas fa-eye fs-7" /> Consulter
                         </button>
                    </>
               )
          }

          // Resp demande rectification / Regul validée → Corriger
          if ([CONST_DECLARATION_SUIVI_TYPE_RESP_DEMANDE_RECTIFICATION, CONST_DECLARATION_SUIVI_TYPE_RESP_VALIDATION_REGUL].includes(etatType)) {
               return (
                    <button className="btn btn-sm btn-danger d-flex align-items-center gap-1" onClick={() => onEdit("recap")}>
                         <i className="fas fa-edit fs-7" /> Corriger
                    </button>
               )
          }

          // Resp validation / Interesse demande regul / Siège demande rectif → Consulter
          if (
               [CONST_DECLARATION_SUIVI_TYPE_RESP_VALIDATION, CONST_DECLARATION_SUIVI_TYPE_INTERESSE_DEMANDE_REGUL, CONST_DECLARATION_SUIVI_TYPE_SIEGE_DEMANDE_RECTIFICATION].includes(
                    etatType
               )
          ) {
               return (
                    <button className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={() => onEdit("recap")}>
                         <i className="fas fa-eye fs-7" /> Consulter
                    </button>
               )
          }

          // Siège validation / Resp rejet regul → Consulter + Régulariser
          if ([CONST_DECLARATION_SUIVI_TYPE_SIEGE_VALIDATION, CONST_DECLARATION_SUIVI_TYPE_RESP_REJET_REGUL].includes(etatType)) {
               return (
                    <>
                         <button className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={() => onEdit("recap")}>
                              <i className="fas fa-eye fs-7" /> Consulter
                         </button>
                         <button className="btn btn-sm btn-danger d-flex align-items-center gap-1" onClick={handleDemandeRegul}>
                              <i className="fas fa-hand fs-7" /> Régulariser
                         </button>
                    </>
               )
          }

          return null
     }

     return (
          <div className={`card shadow-sm border-0 overflow-hidden transition-all ${isExpanded ? "border-primary border" : ""}`} style={{ transition: "border-color 0.2s" }}>
               {/* ── Row header ───────────────────────────────────────────────── */}
               <div className={`card-body py-5 px-6 d-flex align-items-center gap-4 ${canExpand ? "cursor-pointer" : ""}`} onClick={canExpand ? onToggle : undefined}>
                    {/* Avatar */}
                    <div className="symbol symbol-40px flex-shrink-0">
                         <div className="symbol-label bg-light-primary">
                              <span className="fw-bold text-primary fs-7">
                                   {item.consultant.prenom?.[0]}
                                   {item.consultant.nom?.[0]}
                              </span>
                         </div>
                    </div>

                    {/* Name + responsable */}
                    <div className="flex-grow-1 min-w-0">
                         <span className="fw-bold text-gray-800 d-block text-truncate">
                              {item.consultant.nom} {item.consultant.prenom}
                         </span>
                         {item.consultant.sousTraitant?.fournisseurResponsable?.prenomNom && (
                              <span className="text-gray-400 fs-8">{item.consultant.sousTraitant.fournisseurResponsable.prenomNom}</span>
                         )}
                    </div>

                    {/* Working days pill */}
                    <div className="d-none d-md-flex align-items-center gap-2 flex-shrink-0">
                         <span className="badge badge-light-secondary fw-semibold fs-8">
                              <i className="fas fa-calendar-day me-1" />
                              {item.nbJoursTravailles > 0 ? `${item.nbJoursTravailles}j` : "—"}
                         </span>
                    </div>

                    {/* Uploaded files indicators */}
                    <div className="d-none d-md-flex align-items-center gap-2 flex-shrink-0">
                         {/* Factures de prestation */}
                         {item.facturesDePrestation.length > 0 ? (
                              item.facturesDePrestation.map((f: IConsultantDeclarationMensuelleFactureModel, i: number) => (
                                   <span
                                        key={`presta-${i}`}
                                        className="badge badge-light-success fs-8 cursor-pointer"
                                        onClick={e => {
                                             e.stopPropagation()
                                             if (f.googleDriveId) handleViewDoc(f.googleDriveId, f.googleDriveMimeType || "application/pdf")
                                        }}
                                        title="Voir la facture de prestation"
                                   >
                                        <i className="fas fa-file-invoice me-1" />
                                        Facture {item.facturesDePrestation.length > 1 ? i + 1 : ""}
                                   </span>
                              ))
                         ) : (
                              <span className="badge badge-light-warning fs-8">
                                   <i className="fas fa-file-upload me-1" />
                                   Facture manquante
                              </span>
                         )}

                         {/* Factures de frais */}
                         {item.facturesDeFrais.length > 0 &&
                              item.facturesDeFrais.map((f: ConsultantNdfDetailDocumentModel, i: number) => (
                                   <span
                                        key={`frais-${i}`}
                                        className="badge badge-light-info fs-8 cursor-pointer"
                                        onClick={e => {
                                             e.stopPropagation()
                                             if (f.googleDriveID) handleViewDoc(f.googleDriveID, f.googleDriveMimeType || "application/pdf")
                                        }}
                                        title="Voir la facture de frais"
                                   >
                                        <i className="fas fa-receipt me-1" />
                                        Frais {item.facturesDeFrais.length > 1 ? i + 1 : ""}
                                   </span>
                              ))}
                    </div>

                    {/* Status badge */}
                    <div className="flex-shrink-0">
                         {etatConf ? (
                              <span className={`badge badge-light-${etatConf.color} fw-semibold fs-8 px-3 py-2`}>
                                   <i className={`fas ${etatConf.icon} me-1`} />
                                   {etatConf.label}
                              </span>
                         ) : (
                              <span className="badge badge-light-secondary fw-semibold fs-8 px-3 py-2">
                                   <i className="fas fa-clock me-1" />
                                   En attente
                              </span>
                         )}
                    </div>

                    {/* Actions */}
                    <div className="flex-shrink-0 d-flex align-items-center gap-2" onClick={e => e.stopPropagation()}>
                         {renderActions()}
                    </div>

                    {/* Expand chevron */}
                    {canExpand && (
                         <div className="flex-shrink-0 ms-1">
                              <i className={`fas fa-chevron-${isExpanded ? "up" : "down"} text-gray-400 fs-7`} />
                         </div>
                    )}

                    {/* Locked indicator */}
                    {!canExpand && (
                         <div className="flex-shrink-0 ms-1">
                              <i className="fas fa-lock text-gray-300 fs-7" title="Déclaration non modifiable" />
                         </div>
                    )}

                    {/* Doc loading indicator */}
                    {isViewingDoc && (
                         <div className="flex-shrink-0">
                              <span className="spinner-border spinner-border-sm text-primary" />
                         </div>
                    )}
               </div>

               {/* ── Inline saisie panel ───────────────────────────────────────── */}
               {isExpanded && canExpand && (
                    <div className="border-top border-gray-200 bg-light px-6 py-6">
                         <SaisieDeclarationConsultantST consultantId={item.consultant.id!} mois={mois.format("MM")} annee={mois.format("YYYY")} onSuccess={onSuccess} />
                    </div>
               )}
          </div>
     )
}

export default DeclarationsListNewItem
